import type { Building } from "../types";
import { PATH_WIGGLE_VARIANCE } from "../constants";

export type PathSegment = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

export const generatePaths = (buildings: Building[]): PathSegment[] => {
  if (buildings.length < 2) return [];

  // Seeded random number generator for consistent, natural-looking paths
  // Seed based on first building position for determinism
  let seed = Math.floor(buildings[0].x + buildings[0].y);
  const seededRandom = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };

  // Create a continuous path through all buildings
  // We'll create control points that ensure smooth transitions at each flag
  
  // Collect all waypoints (flag positions)
  const waypoints = buildings.map(b => ({ x: b.x, y: b.y }));
  
  // Generate control points for smooth curves between waypoints
  // Each segment gets 2-3 control points in the middle for natural curves
  const allControlPoints: Array<{ x: number; y: number }> = [];
  
  // Always start with first waypoint
  allControlPoints.push({ x: waypoints[0].x, y: waypoints[0].y });
  
  for (let i = 0; i < waypoints.length - 1; i++) {
    const p1 = waypoints[i];
    const p2 = waypoints[i + 1];
    
    // Calculate direction and distance
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const perpX = -dy / distance;
    const perpY = dx / distance;
    
    // Determine number of control points for this segment (based on distance)
    const numControlPoints = Math.max(2, Math.min(3, Math.floor(distance / 2000)));
    
    // Generate control points for this segment
    // Use previous segment's direction to influence curve direction for smoothness
    let prevDirection = 0;
    if (i > 0) {
      const prevDx = waypoints[i].x - waypoints[i - 1].x;
      const prevDy = waypoints[i].y - waypoints[i - 1].y;
      const prevPerpX = -prevDy / Math.sqrt(prevDx * prevDx + prevDy * prevDy);
      const prevPerpY = prevDx / Math.sqrt(prevDx * prevDx + prevDy * prevDy);
      // Calculate if we should curve in same direction as previous segment
      prevDirection = (perpX * prevPerpX + perpY * prevPerpY) > 0 ? 1 : -1;
    }
    
    for (let j = 1; j <= numControlPoints; j++) {
      const t = j / (numControlPoints + 1);
      
      // Base position along straight line
      const baseX = p1.x + dx * t;
      const baseY = p1.y + dy * t;
      
      // Curve strength (stronger in middle, weaker near endpoints)
      const curveStrength = Math.sin(t * Math.PI);
      
      // Generate offset that tends to continue previous curve direction
      // but with some variation for natural look
      const randomOffset = (seededRandom() - 0.5) * 2;
      const directionalBias = i > 0 ? prevDirection * 0.3 : 0; // Bias toward previous direction
      const offset = (randomOffset + directionalBias) * PATH_WIGGLE_VARIANCE * curveStrength;
      
      allControlPoints.push({
        x: baseX + perpX * offset,
        y: baseY + perpY * offset,
      });
    }
    
    // Add the waypoint itself (ensures path goes through flags)
    if (i < waypoints.length - 1) {
      allControlPoints.push({ x: p2.x, y: p2.y });
    }
  }
  
  // Generate smooth path using Catmull-Rom spline through all control points
  const points: Array<{ x: number; y: number }> = [];
  const numSegments = Math.max(30, Math.floor(
    waypoints.reduce((sum, _, i) => {
      if (i === 0) return sum;
      const dx = waypoints[i].x - waypoints[i - 1].x;
      const dy = waypoints[i].y - waypoints[i - 1].y;
      return sum + Math.sqrt(dx * dx + dy * dy);
    }, 0) / 50
  ));
  
  for (let i = 0; i <= numSegments; i++) {
    const t = i / numSegments;
    
    // Map t to control point indices
    const numControlSegments = allControlPoints.length - 1;
    const controlT = t * numControlSegments;
    const controlIndex = Math.floor(controlT);
    const segmentT = controlT - controlIndex;
    
    // Clamp indices
    const i0 = Math.max(0, controlIndex - 1);
    const i1 = Math.max(0, Math.min(numControlSegments, controlIndex));
    const i2 = Math.min(numControlSegments, controlIndex + 1);
    const i3 = Math.min(numControlSegments, controlIndex + 2);
    
    const p0 = allControlPoints[i0];
    const p1 = allControlPoints[i1];
    const p2 = allControlPoints[i2];
    const p3 = allControlPoints[i3];
    
    // Catmull-Rom spline interpolation
    const t2 = segmentT * segmentT;
    const t3 = t2 * segmentT;
    
    const x = 0.5 * (
      (2 * p1.x) +
      (-p0.x + p2.x) * segmentT +
      (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 +
      (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3
    );
    
    const y = 0.5 * (
      (2 * p1.y) +
      (-p0.y + p2.y) * segmentT +
      (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 +
      (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3
    );
    
    points.push({ x, y });
  }
  
  // Create line segments from points
  const paths: PathSegment[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    paths.push({
      x1: points[i].x,
      y1: points[i].y,
      x2: points[i + 1].x,
      y2: points[i + 1].y,
    });
  }

  return paths;
};

